import * as React from 'react';
import ReactFC from 'react-fusioncharts';
import { Col, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

const { useCallback, useMemo } = React;

import Fuse from 'fuse.js';
import FusionCharts from 'fusioncharts';
import Charts from 'fusioncharts/fusioncharts.charts';
import FusionTheme from 'fusioncharts/themes/fusioncharts.theme.fusion';
import {
    DataPoint,
    DataPointNode,
    clearPieChartData
} from '../../../../redux/VisualizationSlice';
import { selectPieChartDataPointNodesSelector } from '../VisualizationSelectors';

// Initialize FusionCharts with required modules
ReactFC.fcRoot(FusionCharts, Charts, FusionTheme);

// Module-level no-op used only so FusionCharts renders drillable slices with a pointer cursor.
// The real drill-down logic is handled entirely by dataPlotClick.
(window as any).__pieChartNoOpLink = () => { /* intentionally empty */ };

const PieChartView = (props: any) => {

    const dispatch = useAppDispatch();

    const pieChartDataPointNodesSelector: DataPointNode | null = useAppSelector(selectPieChartDataPointNodesSelector);

    const isMounted = React.useRef(true);

    const drillDownOffSet = 5;

    const MAX_LABEL_LENGTH = 20;


    const [displayValues, setDisplayValues] = React.useState<Array<{ label: string; displayLabel: string; value: string }>>([]);
    const [lastSelectedDrillDown, setLastSelectedDrillDown] = React.useState<string>("");

    // Chart sizing
    const [chartHeight, setChartHeight] = React.useState<number>(600);

    // Incremented on each data change to force ReactFC unmount/remount,
    // preventing FusionCharts from doing an in-place update mid-render
    const [chartKey, setChartKey] = React.useState<number>(0);

    //Functions

    const getNodesAtLevel = (node: DataPointNode, level: number): DataPointNode[] => {
        if (level === 0) {
            return [node];
        }

        return node && node.children ? node.children.flatMap(childNode => getNodesAtLevel(childNode, level - 1)) : [];
    };

    const findDataPointNodeByX = (node: DataPointNode, xValue: string): DataPointNode | null => {

        if (node.x === xValue) {
            return node;
        }

        for (const child of node.children || []) {
            const result = findDataPointNodeByX(child, xValue);
            if (result) return result;
        }

        return null;
    }

    const convertDataPointNodesToDataPoints = (dataPointNodes: Array<DataPointNode>): Array<DataPoint> => {
        //if the data point node children == 1 replace x with the child x value.
        //if the data point node children > 1, and all of the child x values are the same, replace x with the child x value.
        return dataPointNodes.map((node: DataPointNode) => {
            let xValue = node.x;
            if (node.children && node.children.length === 1) {
                xValue = node.children[0].x;
            } else if (node.children && node.children.length > 1) {
                const allChildXValues = node.children.map(child => child.x);
                const uniqueChildXValues = Array.from(new Set(allChildXValues));
                if (uniqueChildXValues.length === 1) {
                    xValue = uniqueChildXValues[0];
                }
            }
            return {
                x: xValue,
                y: node.y,
                label: xValue
            };
        });
    }

    //clearly this needs to change to use the data point nodes instead of the data points, but for now it will work.
    const setFusionChartDataFromDataPoints = (topValues: Array<DataPoint>) => {
        if (!isMounted.current) return;

        let fusionDataTopValues: Array<{ label: string; displayLabel: string; value: string }> = [];

        for (let i = 0; i < topValues.length; i++) {
            let fusionDataPoint: { label: string; displayLabel: string; value: string; } = {
                label: topValues[i].x,
                displayLabel: getDisplayLabelFromX(topValues[i].x),
                value: topValues[i].y.toString()
            }
            fusionDataTopValues.push(fusionDataPoint);
        }

        // Increment key to force ReactFC to fully unmount and remount,
        // preventing FusionCharts from attempting an in-place data update
        // while the previous render cycle is still active (causes .config crash)
        setChartKey(prev => prev + 1);
        setDisplayValues(fusionDataTopValues);
    }

    const truncateLabel = (label: string): string => {
        if (label.length > MAX_LABEL_LENGTH) {
            return label.substring(0, MAX_LABEL_LENGTH) + '...';
        }
        return label;
    }

    const handlePieSliceClickedNodeHierarchy = useCallback((eventObj: any, dataObj: any) => {
        if (!isMounted.current) return;
        if (!pieChartDataPointNodesSelector) return;
        if (!dataObj) return;

        // dataObj.index corresponds to the position of the slice in the data array we passed in.
        const clickedIndex: number = dataObj.index;
        if (clickedIndex === undefined || clickedIndex < 0 || clickedIndex >= displayValues.length) return;

        // Use the original, untruncated label from displayValues instead of parsing
        // the (possibly truncated) rendered displayValue string.
        const xValue = displayValues[clickedIndex].label;

        let foundParentNode = findDataPointNodeByX(pieChartDataPointNodesSelector, xValue);

        if (foundParentNode && foundParentNode.children && foundParentNode.children.length > 0) {
            const anyChildHasChildren = foundParentNode.children.some(
                child => child.children && child.children.length > 0
            );

            if (!anyChildHasChildren) {
                const childrenHaveDifferentXValues = new Set(foundParentNode.children.map(child => child.x)).size > 1;
                if (!childrenHaveDifferentXValues) return;
            }

            const xAxisDataPoints = convertDataPointNodesToDataPoints(foundParentNode.children);

            setTimeout(() => {
                if (!isMounted.current) return;
                setFusionChartDataFromDataPoints(xAxisDataPoints);
                setLastSelectedDrillDown(foundParentNode!.x);
            }, 0);
        }
    }, [dispatch, pieChartDataPointNodesSelector, props.breadCrumbData, displayValues]);

    // Add this helper function to check if currently DISPLAYED nodes have all-childless children
    const currentLevelHasTransactionParents = useMemo(() => {
        if (!pieChartDataPointNodesSelector) return false;

        let currentLevelTitle: string = "";

        if (props.breadCrumbs && props.breadCrumbs.length > 0) {
            if (props.breadCrumbs.length > 2) {
                currentLevelTitle = props.breadCrumbs[props.breadCrumbs.length - 1];
            }
        }


        if (currentLevelTitle === "") {
            // At root level, check if any of the displayed top-level nodes have all-childless children
            const topLevelNodes = getNodesAtLevel(pieChartDataPointNodesSelector, drillDownOffSet);
            return topLevelNodes.some(node =>
                node.children &&
                node.children.length > 0 &&
                node.children.every(child => !child.children || child.children.length === 0)
            );
        } else {
            // At a drilled-down level, check if any of the currently DISPLAYED nodes have all-childless children
            const currentParentNode = findDataPointNodeByX(pieChartDataPointNodesSelector, currentLevelTitle);
            if (currentParentNode && currentParentNode.children && currentParentNode.children.length > 0) {
                // currentParentNode.children are the nodes currently being DISPLAYED
                // Check if any of them have children that are all leaf nodes
                return currentParentNode.children.some(displayedNode =>
                    displayedNode.children &&
                    displayedNode.children.length > 0 &&
                    displayedNode.children.every(grandchild => !grandchild.children || grandchild.children.length === 0)
                );
            }
        }

        return false;
    }, [pieChartDataPointNodesSelector, props.breadCrumbs]);

    const setPieChartDataFromOffset = () => {

        if (pieChartDataPointNodesSelector == null) return;

        let finalData: Array<DataPoint> = [];

        var dataPointNodesFromOffset = getNodesAtLevel(pieChartDataPointNodesSelector, drillDownOffSet);

        //The offset is set to 5 now, which works out to column F in the taxonomy
        //we now have to take the top 12 data point nodes from this offset and put the rest of the nodes in other category

        //sort the data point nodes by y value descending
        dataPointNodesFromOffset.sort((a, b) => b.y - a.y); // Sort descending by y value

        //get the top 12 data point nodes from the offset
        var top12DataPointNodesAndOtherNode = dataPointNodesFromOffset.slice(0, 12);

        //put the rest of the data point nodes in an "Other" category
        var otherDataPointNodes = dataPointNodesFromOffset.slice(12);

        if (otherDataPointNodes.length > 0) {

            var otherNode: DataPointNode = {

                x: "Other",
                y: otherDataPointNodes.reduce((acc, node) => acc + node.y, 0), // Sum of y values of other nodes,
                children: []
            }
            top12DataPointNodesAndOtherNode.push(otherNode);
        }

        finalData = convertDataPointNodesToDataPoints(top12DataPointNodesAndOtherNode);
        setFusionChartDataFromDataPoints(finalData);

    }

    const isNodeDrillable = useCallback((xValue: string): boolean => {
        if (!pieChartDataPointNodesSelector) return false;

        const foundParentNode = findDataPointNodeByX(pieChartDataPointNodesSelector, xValue);

        if (foundParentNode && foundParentNode.children && foundParentNode.children.length > 0) {
            const anyChildHasChildren = foundParentNode.children.some(
                child => child.children && child.children.length > 0
            );

            if (!anyChildHasChildren) {
                const childrenHaveDifferentXValues = new Set(foundParentNode.children.map(child => child.x)).size > 1;
                if (!childrenHaveDifferentXValues) return false;
            }

            return true;
        }

        return false;
    }, [pieChartDataPointNodesSelector]);

    // Memoize getCaptionValue to prevent recreation on every render
    const getCaptionValue = useCallback(() => {
        return props.selectedDataPoint ? `${props.selectedDataPoint.x}` : "";
    }, [props.selectedDataPoint, props.selectedAccountType.label]);

    const getSubCaptionValue = useCallback(() => {
        let result: string = "";

        if (props.breadCrumbData) {
            if (props.breadCrumbData.length === 3) {
                result = props.formatNumericValue(parseFloat(props.selectedDataPoint.y.toString()));
            } else if (props.breadCrumbData.length > 3) {
                if (pieChartDataPointNodesSelector) {

                    let lastBreadCrumb = props.breadCrumbData[props.breadCrumbData.length - 1];
                    if (lastBreadCrumb) {
                        let foundNodeValue = findDataPointNodeByX(pieChartDataPointNodesSelector, lastBreadCrumb);

                        if (foundNodeValue) {
                            result = `${getDisplayLabelFromX(foundNodeValue.x)} - ${props.formatNumericValue(parseFloat(foundNodeValue.y.toString()))}`
                        }
                    }

                }
            }
        }

        return result;
    }, [pieChartDataPointNodesSelector, props.breadCrumbData,]);

    // Extracts the substring after the last " - " for display purposes.
    // Falls back to the original value if parsing fails or the separator isn't found.
    const getDisplayLabelFromX = (xValue: string): string => {
        if (!xValue) return xValue;

        const separator = " - ";
        const lastIndex = xValue.lastIndexOf(separator);

        if (lastIndex === -1) {
            return xValue;
        }

        const substring = xValue.substring(lastIndex + separator.length);

        return substring.length > 0 ? substring : xValue;
    }

    const pieChartConfig = useMemo(() => ({
        type: "pie3d",
        dataFormat: "json",
        width: "100%",
        height: chartHeight.toString(),
        dataSource: {
            chart: {
                numberPrefix: props.isDollarAmount() ? "$" : "",
                formatNumberScale: "1",
                numberScaleValue: "1000,1000,1000",
                numberScaleUnit: "K,M,B",
                decimals: "2",
                forceDecimals: "2",
                theme: "fusion",
                showPercentValues: "1",
                showPercentInTooltip: "1",
                showValues: "1",
                showLabels: "1",
                enableSmartLabels: "1",
                enableSlicing: currentLevelHasTransactionParents ? "1" : "0",
                legendNumColumns: 5,
                chartBottomMargin: "16", // ~1rem worth of pixel spacing between pie and legend below it
                legendItemPadding: "10", //space between legend items
            },
            data: displayValues.map(item => {
                const drillable = isNodeDrillable(item.label);
                return {
                    label: (drillable ? "* " : "") + item.displayLabel,
                    value: item.value,
                    toolText: `${item.displayLabel}: ${props.formatNumericValue(parseFloat(item.value))}`,
                    link: drillable ? "javascript:__pieChartNoOpLink()" : ""
                };
            })
        },
        events: {
            dataPlotClick: handlePieSliceClickedNodeHierarchy
        }
    }), [
        getCaptionValue,  // Use the memoized function
        props.selectedFiscalYear.value,
        displayValues,
        handlePieSliceClickedNodeHierarchy,
        props.isDollarAmount,
        props.formatNumericValue,
        currentLevelHasTransactionParents,
        chartHeight,
        isNodeDrillable,
        props.breadCrumbData,

    ]);

    React.useEffect(() => {

        return () => {
            isMounted.current = false;
            clearPieChartData();
        };

    }, []);

    React.useEffect(() => {

        const updateChartHeight = () => {
            // Calculate height: 70% of viewport height, minimum 500px
            const calculatedHeight = Math.max(500, window.innerHeight * 0.7);
            setChartHeight(calculatedHeight);
        };

        updateChartHeight();
        window.addEventListener('resize', updateChartHeight);

        return () => window.removeEventListener('resize', updateChartHeight);

    }, []);

    React.useEffect(() => {

        if (pieChartDataPointNodesSelector) {
            setPieChartDataFromOffset();
        }

    }, [pieChartDataPointNodesSelector])


    React.useEffect(() => {

        if (lastSelectedDrillDown !== "") {

            // Guard against undefined breadCrumbData / setBreadCrumbData
            if (props.breadCrumbData && props.setBreadCrumbData) {
                let lastBreadCrumb = props.breadCrumbData[props.breadCrumbData.length - 1];
                if (lastBreadCrumb !== lastSelectedDrillDown) {
                    let updatedBreadCrumbData: Array<string> = [...props.breadCrumbData]; // avoid mutating the original array
                    updatedBreadCrumbData.push(lastSelectedDrillDown);
                    props.setBreadCrumbData(updatedBreadCrumbData);
                }
            }

            setLastSelectedDrillDown("");
        }

    }, [lastSelectedDrillDown]);

    React.useEffect(() => {
        //the parent view component handles setting the breadcrumbs,
        //get the last breadcrumb.  If its the same as the selected data point, set pie chart data from offset,
        //if its not, perform the drill down using the title and the last bread crumb
        if (props.rerenderPieChart && props.rerenderPieChart == true) {
            //note the breadcrumb contains the visualization root, and then the bar chart selection so we start at length greater than 2
            if (props.breadCrumbData && props.breadCrumbData.length >= 3) {

                let lastBreadCrumb = props.breadCrumbData[props.breadCrumbData.length - 1];
                if (lastBreadCrumb === props.selectedDataPoint.x) {
                    setPieChartDataFromOffset();
                    props.setRerenderPieChart(false);
                } else {

                    if (pieChartDataPointNodesSelector) {
                        let xAxisDataPoints: Array<DataPoint> = [];

                        let foundParentNode: DataPointNode | null = null;

                        foundParentNode = findDataPointNodeByX(pieChartDataPointNodesSelector, lastBreadCrumb);

                        // Check if the clicked node has children
                        if (foundParentNode && foundParentNode.children && foundParentNode.children.length > 0) {

                            // Check if ANY of the children have their own children (grandchildren exist)
                            const anyChildHasChildren = foundParentNode.children.some(
                                child => child.children && child.children.length > 0
                            );

                            if (!anyChildHasChildren) {
                                // ALL children are childless (leaf nodes/transactions)
                                const childrenHaveDifferentXValues = new Set(foundParentNode.children.map(child => child.x)).size > 1;

                                if (!childrenHaveDifferentXValues) {
                                    //none of the children have different x values, so we don't want to drill down, just let the slice pop out
                                    return;
                                }
                            }

                            // At least one child has children (grandchildren exist) - proceed with drill down
                            xAxisDataPoints = convertDataPointNodesToDataPoints(foundParentNode.children);

                            // Defer to let any active FusionCharts render cycle complete before mutating data
                            const capturedPoints = xAxisDataPoints;
                            setTimeout(() => {
                                if (!isMounted.current) return;
                                setFusionChartDataFromDataPoints(capturedPoints);
                                props.setRerenderPieChart(false);
                            }, 0);
                        }
                    }
                }
            }
        }

    }, [props.rerenderPieChart])

    const PieChartRender = (<React.Fragment>
        <Row>
            <Col md="12">
                <h3 style={{ paddingLeft: ".75rem" }}>{getCaptionValue() + " - " + props.selectedFiscalYear.value}</h3>
                <h5 style={{ paddingLeft: ".75rem" }}>{getSubCaptionValue()}</h5>
            </Col>
        </Row>
        <Row>
            <Col>
                {displayValues && displayValues.length > 0 && (
                    <ReactFC
                        key={chartKey}
                        id="pie-chart-main"
                        {...pieChartConfig}
                    />
                )}
            </Col>
        </Row>
    </React.Fragment>)

    return (
        <>
            {PieChartRender}
        </>
    );

};

export default PieChartView;
